import { html, nothing, type TemplateResult } from "lit";
import type { SceneDebugNode } from "./sceneDebugModel";

export function renderSceneDebugTree(opts: {
  nodes: SceneDebugNode[];
  selectedId: string | null;
  expanded: Set<string>;
  query: string;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}): TemplateResult {
  return html`
    <ul class="sd-tree" role="tree">
      ${opts.nodes.map((node) =>
        renderNode(node, opts, 0),
      )}
    </ul>
  `;
}

function renderNode(
  node: SceneDebugNode,
  opts: {
    selectedId: string | null;
    expanded: Set<string>;
    query: string;
    onToggle: (id: string) => void;
    onSelect: (id: string) => void;
  },
  depth: number,
): TemplateResult {
  const hasChildren = node.childCount > 0;
  const expanded = !!opts.query || opts.expanded.has(node.id);
  const selected = opts.selectedId === node.id;

  return html`
    <li role="treeitem" aria-expanded=${hasChildren ? String(expanded) : nothing}>
      <button
        type="button"
        class="sd-tree-row ${selected ? "is-selected" : ""}"
        style="padding-left:${6 + depth * 12}px"
        @click=${() => opts.onSelect(node.id)}
      >
        ${hasChildren
          ? html`
              <span
                class="sd-tree-toggle ${expanded ? "is-open" : ""}"
                @click=${(event: Event) => {
                  event.stopPropagation();
                  opts.onToggle(node.id);
                }}
              >
                <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true">
                  <path
                    d="M2.75 1.5 5.5 4 2.75 6.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            `
          : html`<span class="sd-tree-toggle is-leaf"></span>`}
        <span class="sd-tree-badge">${badge(node.type)}</span>
        <span class="sd-tree-name">${node.name}</span>
        <span class="sd-tree-meta">${node.childCount}</span>
      </button>
      ${hasChildren && expanded
        ? html`
            <ul role="group">
              ${node.children.map((child) => renderNode(child, opts, depth + 1))}
            </ul>
          `
        : nothing}
    </li>
  `;
}

function badge(type: string): string {
  if (type.includes("Mesh") || type === "SkinnedMesh") return "Mesh";
  if (type.includes("Light")) return "Light";
  if (type.includes("Camera")) return "Cam";
  if (type === "Group" || type === "Object3D") return "Grp";
  if (type === "Scene") return "Scene";
  if (type.includes("Line")) return "Line";
  if (type === "Points") return "Pts";
  return type.slice(0, 4);
}
